import { ComputedDatum, DatumId, Pie, PieCustomLayerProps } from '@nivo/pie';
import { BriefcaseIcon, MessageSquareTextIcon, PieChartIcon, UserCogIcon } from 'lucide-react';
import { memo, useCallback, useMemo, useState } from 'react';
import DebouncedResizeContainer from '@/components/DebouncedResizeContainer';
import { numberToLocaleString } from '@/lib/utils';
import { sanctionCategories, sanctionKinds, type SanctionKind } from '@/lib/sanctionCategories';
import type { SanctionCountsType, SanctionStatsAdminType, SanctionStatsJobType, SanctionStatsReasonType } from '@shared/otherTypes';
import { StatsCardMessage, StatsLoadingSpinner } from './SanctionStatsGenericSubcards';
import { HoverTip } from '@/components/ui/tooltip';


const TOP_ADMINS_SHOWN = 10;
const TOP_JOBS_SHOWN = 10;


type CardShellProps = {
    title: string;
    icon: React.ReactNode;
    children: React.ReactNode;
};

function CardShell({ title, icon, children }: CardShellProps) {
    return (
        <div className="md:rounded-hud border-edge bg-card shadow-surface flex flex-col">
            <div className="flex flex-row items-center justify-between px-1 sm:px-4 border-b rounded-t-[inherit] bg-secondary/35">
                <div className="flex items-center py-2 space-x-2">
                    <div className='hidden xs:block'>{icon}</div>
                    <h2 className="font-mono text-sm">{title}</h2>
                </div>
            </div>
            {children}
        </div>
    );
}


type PieDatum = {
    id: SanctionKind;
    label: string;
    value: number;
    color: string;
};

type PieCenterTextProps = PieCustomLayerProps<PieDatum> & {
    active?: ComputedDatum<PieDatum>;
};

//Same idea as the dashboard player drops chart: the hole carries the numbers
const PieCenterText = ({ centerX, centerY, dataWithArc, innerRadius, active }: PieCenterTextProps) => {
    if (active) {
        return (<>
            <circle cx={centerX} cy={centerY} r={innerRadius * 0.95} fill={active.color} />
            <text
                x={centerX} y={centerY - 14}
                textAnchor="middle" dominantBaseline="central"
                className='text-lg font-bold' opacity={0.75}
            >
                {active.data.label}
            </text>
            <text
                x={centerX} y={centerY + 14}
                textAnchor="middle" dominantBaseline="central"
                className='text-xl font-bold' opacity={0.75}
            >
                {numberToLocaleString(active.data.value)}
            </text>
        </>);
    }
    const total = dataWithArc.reduce((acc, d) => acc + d.data.value, 0);
    return (<>
        <text
            x={centerX} y={centerY - 14}
            textAnchor="middle" dominantBaseline="central"
            className='text-lg font-bold fill-muted-foreground'
        >
            Total:
        </text>
        <text
            x={centerX} y={centerY + 14}
            textAnchor="middle" dominantBaseline="central"
            className='text-2xl font-bold fill-muted-foreground'
        >
            {numberToLocaleString(total)}
        </text>
    </>);
};


/**
 * Share of each sanction kind in the selected window
 */
const TypePieChart = memo(({ totals, width, height }: { totals: SanctionCountsType, width: number, height: number }) => {
    const [activeId, setActiveId] = useState<DatumId | null>(null);
    const data = useMemo(() => {
        return sanctionKinds
            .filter((kind) => totals[kind] > 0)
            .map((kind) => ({
                id: kind,
                label: sanctionCategories[kind].label,
                value: totals[kind],
                color: sanctionCategories[kind].color,
            })) satisfies PieDatum[];
    }, [totals]);

    const CenterLayer = useCallback((allArgs: PieCustomLayerProps<PieDatum>) => {
        if (!activeId) return PieCenterText(allArgs);
        const active = allArgs.dataWithArc.find(d => d.id === activeId);
        return PieCenterText({ ...allArgs, active });
    }, [activeId]);

    if (!width || !height) return null;
    return (
        //the arcs react to hover, so they get the pointer cursor
        <div className="size-full [&_path]:cursor-pointer">
            <Pie
                width={width}
                height={height}
                data={data}
                theme={{
                    text: { fontSize: '12px', fontWeight: 600 },
                }}
                margin={{ top: 16, right: 16, bottom: 16, left: 16 }}
                innerRadius={0.6}
                padAngle={1.75}
                cornerRadius={4}
                activeId={activeId}
                onActiveIdChange={setActiveId}
                activeOuterRadiusOffset={6}
                colors={{ datum: 'data.color' }}
                borderWidth={0}
                enableArcLinkLabels={false}
                layers={['arcs', 'arcLabels', CenterLayer]}
                arcLabelsSkipAngle={20}
                arcLabelsTextColor={'#08090B'}
                tooltip={() => null}
            />
        </div>
    );
});


type SanctionsBreakdownCardsProps = {
    isError?: boolean;
    totals?: SanctionCountsType;
    byAdmin?: SanctionStatsAdminType[];
    byJob?: SanctionStatsJobType[];
    topReasons?: SanctionStatsReasonType[];
};

function SanctionsBreakdownCards({ isError, totals, byAdmin, byJob, topReasons }: SanctionsBreakdownCardsProps) {
    const [pieSize, setPieSize] = useState({ width: 0, height: 0 });

    const totalSanctions = totals
        ? sanctionKinds.reduce((acc, kind) => acc + totals[kind], 0)
        : 0;
    const shownAdmins = byAdmin?.slice(0, TOP_ADMINS_SHOWN) ?? [];
    const topAdminTotal = shownAdmins[0]?.total ?? 0;
    const topReasonCount = topReasons?.[0]?.count ?? 0;
    const shownJobs = byJob?.slice(0, TOP_JOBS_SHOWN) ?? [];
    const topJobTotal = shownJobs[0]?.total ?? 0;

    return (<>
        <div className="grid gap-4 md:gap-6 lg:grid-cols-2">
            <CardShell title="By Type" icon={<PieChartIcon className="size-4" />}>
                <div className="h-64 max-h-64">
                    {!totals ? (
                        <StatsLoadingSpinner isError={isError} />
                    ) : totalSanctions === 0 ? (
                        <StatsCardMessage message="No sanctions in this period." />
                    ) : (
                        <DebouncedResizeContainer onDebouncedResize={setPieSize}>
                            <TypePieChart totals={totals} width={pieSize.width} height={pieSize.height} />
                        </DebouncedResizeContainer>
                    )}
                </div>
            </CardShell>

            <CardShell title="Top Admins" icon={<UserCogIcon className="size-4" />}>
                <div className="h-64 max-h-64 overflow-auto">
                    {!byAdmin ? (
                        <StatsLoadingSpinner isError={isError} />
                    ) : !shownAdmins.length ? (
                        <StatsCardMessage message="No sanctions in this period." />
                    ) : (
                        <div className="divide-y divide-edge">
                            {shownAdmins.map((admin) => (
                                <div key={admin.name} className="px-3 py-2">
                                    <div className="flex items-center justify-between gap-3 pb-1">
                                        <span className="truncate-flex text-sm">{admin.name}</span>
                                        <span className="shrink-0 font-highlight tabular text-sm">
                                            {numberToLocaleString(admin.total)}
                                        </span>
                                    </div>
                                    <div
                                        className="flex h-1.5 gap-px overflow-hidden rounded-bar bg-track"
                                        style={{ width: `${topAdminTotal ? (admin.total / topAdminTotal) * 100 : 0}%` }}
                                    >
                                        {sanctionKinds.map((kind) => admin[kind] > 0 ? (
                                            <HoverTip
                                                key={kind}
                                                label={`${sanctionCategories[kind].label}: ${admin[kind]}`}
                                            >
                                                <span
                                                    className="cursor-pointer"
                                                    style={{
                                                        backgroundColor: sanctionCategories[kind].color,
                                                        width: `${(admin[kind] / admin.total) * 100}%`,
                                                    }}
                                                />
                                            </HoverTip>
                                        ) : null)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </CardShell>
        </div>

        <CardShell title="By Job" icon={<BriefcaseIcon className="size-4" />}>
            <div className="max-h-80 overflow-auto">
                {!byJob ? (
                    <StatsLoadingSpinner isError={isError} />
                ) : !shownJobs.length ? (
                    <StatsCardMessage message="No jobs reported for these sanctions." />
                ) : (
                    <div className="divide-y divide-edge">
                        {shownJobs.map((entry) => (
                            <div key={entry.job} className="px-3 py-2">
                                <div className="flex items-center justify-between gap-3 pb-1">
                                    <span className="truncate-flex text-sm">{entry.job}</span>
                                    <span className="shrink-0 font-highlight tabular text-sm">
                                        {numberToLocaleString(entry.total)}
                                    </span>
                                </div>
                                <div
                                    className="flex h-1.5 gap-px overflow-hidden rounded-bar bg-track"
                                    style={{ width: `${topJobTotal ? (entry.total / topJobTotal) * 100 : 0}%` }}
                                >
                                    {sanctionKinds.map((kind) => entry[kind] > 0 ? (
                                        <HoverTip
                                            key={kind}
                                            label={`${sanctionCategories[kind].label}: ${entry[kind]}`}
                                        >
                                            <span
                                                className="cursor-pointer"
                                                style={{
                                                    backgroundColor: sanctionCategories[kind].color,
                                                    width: `${(entry[kind] / entry.total) * 100}%`,
                                                }}
                                            />
                                        </HoverTip>
                                    ) : null)}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </CardShell>

        <CardShell title="Top Reasons" icon={<MessageSquareTextIcon className="size-4" />}>
            <div className="max-h-80 overflow-auto">
                {!topReasons ? (
                    <StatsLoadingSpinner isError={isError} />
                ) : !topReasons.length ? (
                    <StatsCardMessage message="No sanctions in this period." />
                ) : (
                    <div className="divide-y divide-edge">
                        {topReasons.map((entry) => (
                            <div key={entry.reason} className="flex items-center gap-3 px-3 py-2">
                                <div className="min-w-0 grow">
                                    <HoverTip label={entry.reason}>
                                        <div className="truncate-flex text-sm">
                                            {entry.reason}
                                        </div>
                                    </HoverTip>
                                    <div className="mt-1 h-1.5 rounded-bar bg-track">
                                        <div
                                            className="h-full rounded-bar bg-accent"
                                            style={{ width: `${topReasonCount ? (entry.count / topReasonCount) * 100 : 0}%` }}
                                        />
                                    </div>
                                </div>
                                <span className="shrink-0 font-highlight tabular text-sm">
                                    {numberToLocaleString(entry.count)}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </CardShell>
    </>);
}

export default memo(SanctionsBreakdownCards);
